import { useMemo, type JSX } from 'react';
import Avatar from '../Avatar/Avatar';
import Tooltip, { TooltipProps } from '../Tooltip/Tooltip';

export type Props = {
  user: {
    firstName: string;
    lastName: string;
    emailAddress?: string | null;
    userColour?: string | null;
    imgSrc?: string | null;
  };
  sizeVariant?: React.ComponentProps<typeof Avatar>['sizeVariant'];
  enableUserInfoTooltip?: boolean;
  tooltipPlacement?: TooltipProps['placement'];
  borderColourVariant?: React.ComponentProps<
    typeof Avatar
  >['borderColourVariant'];
};

const UserAvatar = ({
  user,
  sizeVariant,
  enableUserInfoTooltip,
  tooltipPlacement,
  borderColourVariant,
}: Props): JSX.Element => {
  const userInitials = useMemo(() => {
    const initialsArray: string[] = [];
    if (user.firstName?.length) initialsArray.push(user.firstName[0]);
    if (user.lastName?.length) initialsArray.push(user.lastName[0]);

    return initialsArray.join('').toUpperCase();
  }, [user]);

  const tooltipContent = useMemo(() => {
    if (!enableUserInfoTooltip) return;

    return (
      <div className="flex flex-col">
        <strong>
          {user.firstName} {user.lastName}
        </strong>
        {user.emailAddress}
      </div>
    );
  }, [user, enableUserInfoTooltip]);

  if (enableUserInfoTooltip) {
    return (
      <Tooltip tooltipContent={tooltipContent} placement={tooltipPlacement}>
        <Avatar
          sizeVariant={sizeVariant}
          placeholderInitials={userInitials}
          colour={user.userColour}
          borderColourVariant={borderColourVariant}
          imgSrc={user.imgSrc}
        />
      </Tooltip>
    );
  }

  return (
    <Avatar
      sizeVariant={sizeVariant}
      placeholderInitials={userInitials}
      colour={user.userColour}
      borderColourVariant={borderColourVariant}
      imgSrc={user.imgSrc}
    />
  );
};

export default UserAvatar;
